import React, {PureComponent} from "react";
import RewardEntity from "../../data/entities/RewardEntity";
import {GameEntity} from "../../data/entities/GameEntity";
import {FormattedMessage} from "react-intl";
import {Button, Col} from "reactstrap";
import classNames from "classnames";
import {ScaleLoader} from "react-spinners";
import {AdobeAnalyticsContext} from "../../context/AdobeAnalyticsContext";

const locked = require("../../resources/img/locked.svg");

interface Props {
    reward: RewardEntity;
    games: {[id: number]: GameEntity};
    period: number;
    rewardsLeft: number;
    claimReward: (index: number, callback: (success: boolean) => void) => void;
    index: number;
}

interface State {
    showAction: boolean;
    showClaimConfirm: boolean;
    loading: boolean;
}

export default class Reward extends PureComponent<Props> {
    static contextType = AdobeAnalyticsContext;
    context!: React.ContextType<typeof AdobeAnalyticsContext>;

    private node: HTMLDivElement | null = null;
    private canHover: boolean = !matchMedia('(hover: none)').matches;

    state: State = {
        showAction: false,
        showClaimConfirm: false,
        loading: false,
    };

    componentDidMount(): void {
        window.addEventListener('click', this.handleClick);
    }

    componentWillUnmount(): void {
        window.removeEventListener('click', this.handleClick);
    }

    render(): JSX.Element {
        let rewardClasses = classNames({
            "reward": true,
            "loading": this.state.loading,
            "unavailable": this.props.period !== 0,
            "locked": this.isLocked(),
            "show-action": this.state.showAction,
            "show-claim-confirm": this.state.showClaimConfirm,
            "can-hover": this.canHover,
        });

        let rewardActionClasses = classNames({
            "action": true,
            "p-4": true,
            "grey": this.props.rewardsLeft <= 0 || this.props.reward.claimed,
        });

        return (
            <Col xl={3} lg={4} md={6} sm={6} xs={12} className="reward-wrapper">
                <div className={rewardClasses} ref={node => this.node = node}>
                    <div className="reward-image">
                        <div className="img" style={{backgroundImage: `url(${this.props.reward.image})`}}></div>
                        {this.statusBadge()}
                        {
                            this.isLocked() &&
                                <img src={locked} alt="Locked" className="locked-icon"/>
                        }
                    </div>
                    <div className="reward-title">
                        <p className="reward-game">
                            {this.gameName()}
                        </p>
                        <p className="reward-name">{this.props.reward.name}</p>
                    </div>

                    <div className={rewardActionClasses} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        {this.rewardActionOverlay()}
                    </div>

                    <div className="claim-confirm p-4" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <p className="mb-5"><strong><FormattedMessage id="rewards.claim_confirm"/></strong></p>
                        <Button color="link" className="cancel" onClick={this.hideClaimConfirm}>
                            <FormattedMessage id="rewards.close"/>
                        </Button>
                    </div>

                    {
                        this.state.loading &&
                        <div className="reward-loader">
                            <ScaleLoader color={'#fff'}/>
                        </div>
                    }
                </div>
            </Col>
        );
    }

    statusBadge = (): JSX.Element => {
        let statusBadgeClass: string = "available";
        let statusBadgeText: JSX.Element = <FormattedMessage id="rewards.available"/>;

        if (this.props.reward.claimed) {
            statusBadgeClass = "claimed";
            statusBadgeText = <FormattedMessage id="rewards.claimed"/>;
        } else if (this.props.period > 0 || this.props.rewardsLeft <= 0) {
            statusBadgeClass = "unavailable";
            statusBadgeText = <FormattedMessage id="rewards.locked"/>;
        }

        return (
            <span className={`badge ${statusBadgeClass}`}>
                {statusBadgeText}
            </span>
        );
    };

    isLocked = (): boolean => {
        return (this.props.rewardsLeft <= 0 || this.props.period !== 0) && !this.props.reward.claimed
    };

    gameName = (): JSX.Element => {
        if (this.props.reward.gameId && this.props.games[this.props.reward.gameId]) {
            return <>{this.props.games[this.props.reward.gameId].name}</>;
        } else {
            return <>&nbsp;</>;
        }
    };

    rewardActionOverlay = (): JSX.Element => {
        if (this.props.reward.claimed) {
            return (
                <>
                    <p className="mb-5"><strong><FormattedMessage id="rewards.already_claimed"/></strong></p>
                    <Button color="link" className="cancel" onClick={this.hideAction}>
                        <FormattedMessage id="rewards.close"/>
                    </Button>
                </>
            );
        } else if (this.props.rewardsLeft > 0) {
            return (
                <>
                    <p className="mb-5">
                        <strong><FormattedMessage id="rewards.confirm"/></strong>
                    </p>
                    <Button className="confirm mb-3" onClick={this.claimReward}>
                        <FormattedMessage id="rewards.claim"/>
                    </Button>
                    <Button color="link" className="cancel" onClick={this.hideAction}>
                        <FormattedMessage id="rewards.cancel"/>
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <p className="mb-5"><strong><FormattedMessage id="rewards.none_left"/></strong></p>
                    <Button color="link" className="cancel" onClick={this.hideAction}>
                        <FormattedMessage id="rewards.close"/>
                    </Button>
                </>
            );
        }
    };

    handleClick = (e: MouseEvent): void => {
        if (this.node && this.node.contains(e.target as Node)) {
            if (this.props.period === 0 && !this.state.loading) {
                this.context.track("clickevent", {location: `rewards : ${this.props.index}`, category: "interaction", action: "claim reward : picture"});
                this.setState({showAction: true});
            }
        } else if (this.state.showAction) {
            this.hideAction();
        } else if (this.state.showClaimConfirm) {
            this.hideClaimConfirm();
        }
    };

    claimReward = (): void => {
        if (!this.state.loading) {
            this.context.track("clickevent", {location: `rewards : ${this.props.index}`, category: "interaction", action: "claim reward : cta"});
            this.setState({loading: true, showAction: false}, () => {
                this.props.claimReward(this.props.index, (success: boolean) => {
                    this.setState({loading: false, showClaimConfirm: success});
                });
            });
        }
    };

    hideAction = (): void => {
        this.context.track("clickevent", {location: `rewards : ${this.props.index}`, category: "interaction", action: "claim reward : cancel"});
        this.setState({showAction: false});
    };

    hideClaimConfirm = (): void => {
        this.setState({showClaimConfirm: false});
    };
}